import React, { useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { flow, get } from 'lodash-es';
import { connect } from 'react-redux';
import { TrackingHandler } from 'client/tracking/handler';
import { PersonalizedSearchMap } from 'client/tracking/maps/personalized-search';
import { ApiMetrics, withMetrics } from 'client/utils/metrics-hoc';
import { InventoryDependencyContext } from 'site-modules/shared/components/inventory/inventory-dependency/inventory-dependency-context';
import { personalizeSearchDependencyContextValue } from 'site-modules/shared/components/inventory/inventory-dependency/personalize-search-dependency-context-value';
import { LoadingCoordinatorContextProvider } from 'site-modules/personalized-search/context/loading-context/loading-coordinator-context-provider/loading-coordinator-context-provider';
import { PersonalizedSearchHeader } from 'site-modules/personalized-search/components/personalized-search-header/personalized-search-header';
import { PersonalizedSearchContent } from 'site-modules/personalized-search/components/personalized-search-content/personalized-search-content';
import { useUpdateMetaTags } from 'site-modules/shared/hooks/use-update-meta-tags';
import { DEFAULT_DESCRIPTION } from 'site-modules/shared/constants/seo';
import { PersonalizedSearchRedirect } from 'site-modules/personalized-search/components/personalized-search-redirect/personalized-search-redirect';
import { useSearchSubmit } from 'site-modules/personalized-search/hooks/use-search-submit';
import { useSearchResults } from 'site-modules/personalized-search/hooks/use-search-results';

import './personalized-search.scss';

function PersonalizedSearchUI({ isMobile = false, apiMetrics, setModelValue }) {
  const { input, pageStruct, location, searchId } = useSearchResults();

  useUpdateMetaTags({
    seoHeadContent: { title: `${input || 'Search'} | Edmunds`, description: DEFAULT_DESCRIPTION },
  });

  useEffect(() => {
    TrackingHandler.useMap(PersonalizedSearchMap);
  }, []);

  useSearchSubmit({ location, setModelValue });

  return (
    <InventoryDependencyContext.Provider value={personalizeSearchDependencyContextValue}>
      <LoadingCoordinatorContextProvider>
        <main id="main-content" className="personalized-search bg-blue-100">
          <PersonalizedSearchHeader
            isMobile={isMobile}
            input={input}
            searchId={searchId}
            apiMetrics={apiMetrics}
            trapFocus={!!pageStruct}
            location={location}
          />
          <PersonalizedSearchRedirect searchId={searchId} location={location} isError={pageStruct === null} />
          <PersonalizedSearchContent isMobile={isMobile} />
        </main>
      </LoadingCoordinatorContextProvider>
    </InventoryDependencyContext.Provider>
  );
}

PersonalizedSearchUI.propTypes = {
  apiMetrics: ApiMetrics.isRequired,
  isMobile: PropTypes.bool,
  setModelValue: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  isMobile: get(state, 'mobile'),
});

export const PersonalizedSearch = flow(memo, withMetrics, connect(mapStateToProps))(PersonalizedSearchUI);
